import { ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { DeliveryTypeService } from '../../api/services/deliveryTypeService';
import { PaymentTypeService } from '../../api/services/paymentTypeService';
import { DeliveryTypeResponse } from '../../types/api/response/deliverytyperesponse';
import { UpdateDeliveryTypeRequest } from '../../types/api/request/deliverytyperequest';

const emptyForm = {
  label: '',
  description: '',
  open_time: '',
  close_time: '',
  display_order: '',
  is_active: true,
  payment_types_all: true,
  payment_types: [] as string[],
  min_client_version: '',
};

const toTimeInputValue = (time: string | null) => (time ? time.slice(0, 5) : '');

const parsePaymentTypes = (paymentTypes: string | null) =>
  !paymentTypes
    ? { all: true, list: [] as string[] }
    : {
        all: false,
        list: paymentTypes
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box display="flex" justifyContent="space-between" gap={2} py={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} textAlign="right">
      {value ?? '—'}
    </Typography>
  </Box>
);

export const DeliveryTypesPage = () => {
  const dispatch = useDispatch();
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryTypeResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DeliveryTypeResponse | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<DeliveryTypeResponse | null>(null);
  const [viewItem, setViewItem] = useState<DeliveryTypeResponse | null>(null);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState<string[]>([]);

  const fetchDeliveryTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DeliveryTypeService.listDeliveryTypes();
      setDeliveryTypes(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load delivery types.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchPaymentTypeOptions = useCallback(async () => {
    try {
      const data = await PaymentTypeService.listPaymentTypes();
      setPaymentTypeOptions(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load payment types.',
          severity: 'error',
        }),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    fetchDeliveryTypes();
    fetchPaymentTypeOptions();
  }, [fetchDeliveryTypes, fetchPaymentTypeOptions]);

  const openEdit = (item: DeliveryTypeResponse) => {
    setEditingItem(item);
    const { all, list } = parsePaymentTypes(item.payment_types);
    setForm({
      label: item.label,
      description: item.description ?? '',
      open_time: toTimeInputValue(item.open_time),
      close_time: toTimeInputValue(item.close_time),
      display_order: String(item.display_order),
      is_active: item.is_active,
      payment_types_all: all,
      payment_types: list,
      min_client_version: item.min_client_version != null ? String(item.min_client_version) : '',
    });
    setEditOpen(true);
  };

  const togglePaymentType = (code: string) => {
    setForm((prev) => ({
      ...prev,
      payment_types: prev.payment_types.includes(code)
        ? prev.payment_types.filter((c) => c !== code)
        : [...prev.payment_types, code],
    }));
  };

  const handleSubmit = async () => {
    if (!editingItem) return;

    const missingFields: string[] = [];
    if (!form.label.trim()) missingFields.push('Label');
    if (!form.display_order.trim()) missingFields.push('Display Order');
    if (!form.payment_types_all && form.payment_types.length === 0)
      missingFields.push('Payment Types');
    if (missingFields.length > 0) {
      dispatch(
        showNotification({
          message: `Required fields are missing: ${missingFields.join(', ')}.`,
          severity: 'error',
        }),
      );
      return;
    }

    setSaving(true);
    try {
      const body: UpdateDeliveryTypeRequest = {
        label: form.label,
        description: form.description || undefined,
        open_time: form.open_time || undefined,
        close_time: form.close_time || undefined,
        display_order: parseInt(form.display_order),
        is_active: form.is_active,
        payment_types: form.payment_types_all ? undefined : form.payment_types.join(','),
        clear_payment_types: form.payment_types_all ? true : undefined,
        min_client_version: form.min_client_version.trim()
          ? parseInt(form.min_client_version)
          : undefined,
        clear_min_client_version: form.min_client_version.trim() ? undefined : true,
      };
      await DeliveryTypeService.updateDeliveryType(editingItem.id, body);
      dispatch(showNotification({ message: 'Delivery type updated.', severity: 'success' }));
      setEditOpen(false);
      fetchDeliveryTypes();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to save delivery type.',
          severity: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusClick = (item: DeliveryTypeResponse) => {
    if (item.is_active) {
      setConfirmDeactivate(item);
    } else {
      toggleStatus(item, true);
    }
  };

  const toggleStatus = async (item: DeliveryTypeResponse, newStatus: boolean) => {
    try {
      await DeliveryTypeService.updateDeliveryType(item.id, { is_active: newStatus });
      fetchDeliveryTypes();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to update status.',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box>
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && deliveryTypes.length === 0 && (
        <Box py={8} textAlign="center" color="text.secondary">
          <Typography variant="h6">No delivery types found</Typography>
        </Box>
      )}

      {!loading && deliveryTypes.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Open Time</TableCell>
                <TableCell>Close Time</TableCell>
                <TableCell>Display Order</TableCell>
                <TableCell>Min. App Version</TableCell>
                <TableCell>Payment Types</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryTypes.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <code>{item.code}</code>
                  </TableCell>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>{item.description ?? '—'}</TableCell>
                  <TableCell>{toTimeInputValue(item.open_time) || '—'}</TableCell>
                  <TableCell>{toTimeInputValue(item.close_time) || '—'}</TableCell>
                  <TableCell>{item.display_order}</TableCell>
                  <TableCell>{item.min_client_version ?? '—'}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.payment_types || 'All'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.is_active ? 'Active' : 'Inactive'}
                      color={item.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleStatusClick(item)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setViewItem(item)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <Button size="small" onClick={() => openEdit(item)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Delivery Type</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Code"
              value={editingItem?.code ?? ''}
              disabled
              fullWidth
              helperText="Delivery type codes are fixed and cannot be changed."
            />
            <TextField
              label="Label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
            />
            <TextField
              label="Open Time"
              type="time"
              value={form.open_time}
              onChange={(e) => setForm({ ...form, open_time: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Close Time"
              type="time"
              value={form.close_time}
              onChange={(e) => setForm({ ...form, close_time: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={form.display_order}
              onChange={(e) => setForm({ ...form, display_order: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Min. App Version"
              type="number"
              value={form.min_client_version}
              onChange={(e) => setForm({ ...form, min_client_version: e.target.value })}
              fullWidth
              helperText="App must be on this version or newer to use this delivery type. Leave blank to allow all versions."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.payment_types_all}
                  onChange={(e) => setForm({ ...form, payment_types_all: e.target.checked })}
                />
              }
              label="Support all payment types"
            />
            {!form.payment_types_all && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Supported Payment Types
                </Typography>
                <FormGroup row>
                  {paymentTypeOptions.map((code) => (
                    <FormControlLabel
                      key={code}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.payment_types.includes(code)}
                          onChange={() => togglePaymentType(code)}
                        />
                      }
                      label={code}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        <DialogTitle>Deactivate Delivery Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating {confirmDeactivate?.label} will hide it from checkout. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              if (confirmDeactivate) toggleStatus(confirmDeactivate, false);
              setConfirmDeactivate(null);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* View details modal */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delivery Type Details</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column">
            <DetailRow label="Code" value={viewItem?.code} />
            <Divider />
            <DetailRow label="Label" value={viewItem?.label} />
            <Divider />
            <DetailRow label="Description" value={viewItem?.description} />
            <Divider />
            <DetailRow label="SKU" value={viewItem?.sku} />
            <Divider />
            <DetailRow label="Charge Description" value={viewItem?.charge_description} />
            <Divider />
            <DetailRow
              label="Open Time"
              value={toTimeInputValue(viewItem?.open_time ?? null) || '—'}
            />
            <Divider />
            <DetailRow
              label="Close Time"
              value={toTimeInputValue(viewItem?.close_time ?? null) || '—'}
            />
            <Divider />
            <DetailRow label="Display Order" value={viewItem?.display_order} />
            <Divider />
            <DetailRow label="Min. App Version" value={viewItem?.min_client_version} />
            <Divider />
            <DetailRow label="Status" value={viewItem?.is_active ? 'Active' : 'Inactive'} />
            <Divider />
            <Box py={1}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Payment Types
              </Typography>
              {!viewItem?.payment_types ? (
                <Chip label="All" size="small" />
              ) : (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {viewItem?.payment_types
                    ?.split(',')
                    .map((c) => c.trim())
                    .filter(Boolean)
                    .map((c) => (
                      <Chip key={c} label={c} size="small" />
                    ))}
                </Box>
              )}
            </Box>
            <Divider />
            <DetailRow label="Created At" value={viewItem?.created_at} />
            <Divider />
            <DetailRow label="Updated At" value={viewItem?.updated_at} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
